/**
 * How long the presentation surface may stay empty (queue ready, brand cube on screen)
 * before diagnostics raise {@link empty_playback_surface}. Longer than the VBuffer recovery
 * pending stall (3.5s) so recovery gets a chance first; short enough to catch production
 * black-screen stalls (#1338 / #1110).
 */
export const EMPTY_PLAYBACK_SURFACE_FAULT_MS = 10_000

export interface IEmptyPlaybackSurfaceDetectInput {
  documentVisible: boolean
  queueLength: number
  surfaceFrameCount: number
  /** Continuous empty-surface window while queue is non-empty; null when not empty. */
  emptySurfaceStallMs: number | null
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
  weeklyAvailabilitySuppressed: boolean
  /** Fault threshold (defaults to {@link EMPTY_PLAYBACK_SURFACE_FAULT_MS}). */
  thresholdMs?: number
}

export interface IEmptyPlaybackSurfaceStatus {
  suspected: boolean
  /** Stall ms when suspected; otherwise 0. */
  stallMs: number
}

/**
 * Detects the brand-cube / black-screen mode: scheduler queue is ready but nothing has painted.
 */
export function detectEmptyPlaybackSurface(
  input: IEmptyPlaybackSurfaceDetectInput
): IEmptyPlaybackSurfaceStatus {
  const thresholdMs = input.thresholdMs ?? EMPTY_PLAYBACK_SURFACE_FAULT_MS
  if (
    !input.documentVisible
    || input.weeklyAvailabilitySuppressed
    || input.queueLength < 1
    || input.surfaceFrameCount > 0
    || input.emptySurfaceStallMs == null
    || input.suppressBecauseRegistrationResync
    || input.suppressBecauseRegistrationTransitionHold
  ) {
    return { suspected: false, stallMs: 0 }
  }
  const stallMs = Math.max(0, input.emptySurfaceStallMs)
  if (stallMs < thresholdMs) {
    return { suspected: false, stallMs: 0 }
  }
  return { suspected: true, stallMs }
}
